// param-env.js — THE one place a product's parameter environment is built.
//
// SHARED between the server resolver (product-db.js) and its client twin
// (web/product-resolver.js), for the same reason formula.js is: the env was being
// built in five places with the same two lines, and a derived parameter cannot be
// added to five copies without one of them drifting.
//
// A parameter is one of two things (Alex, 2026-08-03):
//
//   CONFIGURABLE (default)  a knob. It has a typical, it can be overridden per
//                           placement or per sub-component, and it is BROADCAST —
//                           it shows up wherever someone is asked to configure this
//                           product.
//
//   INTERNAL                plumbing. "when using it in another product, the weight,
//                           scrap, and rate must not be configurable parameters but
//                           internal!" It is never offered and never overridable. It
//                           may be a plain constant (Scrap = 1.2) or carry a FORMULA
//                           over the other parameters ("sometime, it is cool to have
//                           a parameter I can use for intermediate calculation").
//
// A formula is only allowed on an internal parameter, and that is not a arbitrary
// restriction: a parameter that computes itself is by definition not a knob, so
// letting both be true at once would mean an override silently discarded.
//
// RESOLUTION ORDER. Derived parameters may reference each other, so plain
// left-to-right evaluation is not enough. Constants land first, then derived ones
// are resolved by repeated passes until a pass makes no progress. Anything still
// unresolved is a cycle or a bad formula — it is left OUT of the env (so the
// formulas that use it fail loudly and price nothing) and named in `unresolved`,
// rather than being defaulted to zero, which would price silently and wrongly.

import { evalFormula } from './formula.js';

// isConfigurable(p) — the column is nullable for DBs that predate it, and an
// absent value means the old behaviour: everything is a knob.
export function isConfigurable(p) {
  return p.configurable == null ? true : !!Number(p.configurable);
}

// isDerived(p) — carries a formula instead of a fixed typical.
export function isDerived(p) {
  return !isConfigurable(p) && p.value_expr != null && String(p.value_expr).trim() !== '';
}

// buildParamEnv(params, overrides) -> { env, unresolved }
//
// overrides: { name: value } from a placement's actual_values or a comp's
// param_map. They apply ONLY to configurable parameters — an override aimed at an
// internal one is ignored, because "internal" means exactly that it is not
// something a caller gets to set.
export function buildParamEnv(params = [], overrides = null) {
  const env = {};
  const derived = [];

  for (const p of params) {
    if (isDerived(p)) { derived.push(p); continue; }
    // `!= null` on purpose, matching the `??` the call sites used before this
    // module existed: a null actual_value means "not set", not "set to null".
    const canOverride = overrides && isConfigurable(p) && overrides[p.name] != null;
    env[p.name] = canOverride ? overrides[p.name] : p.typical_value;
  }

  // Fixpoint. At most one parameter can be newly resolved per pass in the worst
  // case (a chain), so `derived.length` passes is the bound.
  let pending = derived;
  for (let pass = 0; pass < derived.length && pending.length; pass++) {
    const still = [];
    for (const p of pending) {
      const v = evalFormula(p.value_expr, env);
      if (v == null || !Number.isFinite(v)) { still.push(p); continue; }
      env[p.name] = v;
    }
    if (still.length === pending.length) break;   // no progress — the rest cannot resolve
    pending = still;
  }

  return { env, unresolved: pending.map(p => p.name) };
}

// The common case, for call sites that only want the values.
export function paramEnv(params, overrides = null) {
  return buildParamEnv(params, overrides).env;
}

// subCostShape(qtyParam, paramMap, eff) -> { overrides, multiplier }
//
// THE rule that tells the two kinds of product apart, in one place so every cost
// path answers it the same way. `eff` is the component's resolved quantity.
//
//   QUANTITY-BASED (qtyParam set) — eff is a SIZE. It is pushed INTO the sub as the
//     driver parameter's value and the multiplier drops to 1. This is what makes a
//     1 kg recipe and a 1000 kg one give the same answer: the typical no longer
//     participates. It also prices fixed costs correctly — a $500 controller stays
//     $500 at any size, which multiplying a unit cost can never express.
//
//   CONFIGURABLE FIXED (qtyParam null) — eff is a COUNT, unchanged from before:
//     the sub resolves at its own params (overridden by param_map where present)
//     and the total is multiplied by eff.
//
// A param_map on a quantity-based product still applies, with the driver winning —
// there is nothing to gain from letting a stale mapping override the size that was
// just typed into Qty.
export function subCostShape(qtyParam, paramMap = null, eff = 1) {
  const map = paramMap && typeof paramMap === 'object' ? paramMap : {};
  if (qtyParam) return { overrides: { ...map, [qtyParam]: eff }, multiplier: 1 };
  return { overrides: map, multiplier: eff };
}
